import * as THREE from 'three';
import { DITHER_GLSL } from '../core/materials.js';

// ---------------------------------------------------------------------------
// The Dutch sky — which, in a country this flat, is most of the view.
// A single inverted sphere: gradient, sun, moon and stars all in one shader.
// ---------------------------------------------------------------------------

const VERT = /* glsl */ `
varying vec3 vDir;
void main() {
    vDir = normalize(position);
    // Strip translation from the view matrix so the dome rides with the camera.
    mat4 v = viewMatrix;
    v[3].xyz = vec3(0.0);
    gl_Position = projectionMatrix * v * vec4(position, 1.0);
    gl_Position.z = gl_Position.w;   // always at the far plane
}
`;

const FRAG = /* glsl */ `
uniform vec3 uSkyColor;
uniform vec3 uHorizonColor;
uniform vec3 uSunColor;
uniform vec3 uSunDir;
uniform float uNight;
uniform float uTime;

varying vec3 vDir;

${DITHER_GLSL}

float hash13(vec3 p) {
    p = fract(p * 0.1031);
    p += dot(p, p.zyx + 31.32);
    return fract((p.x + p.y) * p.z);
}

vec3 hash33(vec3 p) {
    p = fract(p * vec3(0.1031, 0.1030, 0.0973));
    p += dot(p, p.yxz + 33.33);
    return fract((p.xxy + p.yxx) * p.zyx);
}

void main() {
    vec3 dir = normalize(vDir);
    float h = clamp(dir.y * 0.5 + 0.5, 0.0, 1.0);

    // Banded gradient: the dithering keeps the bands from looking like a
    // broken gradient on cheap displays.
    float t = bandDither(pow(clamp(dir.y, 0.0, 1.0), 0.55), 14.0, gl_FragCoord.xy, 1.0);
    vec3 col = mix(uHorizonColor, uSkyColor, t);

    // Glow around the sun, and a hard little disc.
    vec3 sun = normalize(uSunDir);
    float sunDot = max(dot(dir, sun), 0.0);
    col += uSunColor * pow(sunDot, 8.0) * 0.35;
    col += uSunColor * pow(sunDot, 220.0) * 2.2;
    float disc = smoothstep(0.9975, 0.9986, sunDot);
    col = mix(col, uSunColor * 2.4, disc);

    // Moon, opposite the sun, only after dark.
    vec3 moon = -sun;
    float moonDot = max(dot(dir, moon), 0.0);
    float moonDisc = smoothstep(0.9985, 0.9992, moonDot) * uNight;
    col += vec3(0.85, 0.87, 0.95) * moonDisc * 1.6;
    col += vec3(0.35, 0.4, 0.6) * pow(moonDot, 90.0) * uNight * 0.5;

    // --- stars ------------------------------------------------------------
    // Sampled in 3D rather than by projecting dir.xz/dir.y, which stretched
    // every star into a vertical streak as it neared the horizon. Each cell
    // of a 3D lattice holds one jittered point, so stars come out round and
    // evenly spread however you tilt the camera.
    if (uNight > 0.01 && dir.y > -0.05) {
        // Slow wheel about the pole, so the sky turns through the night.
        float a = uTime * 0.006;
        vec3 sd = vec3(
            dir.x * cos(a) - dir.z * sin(a),
            dir.y,
            dir.x * sin(a) + dir.z * cos(a)
        );

        // Project onto the face of a cube and grid *that*. A raw 3D lattice
        // only grazes the view sphere, so almost every cell's star sat off
        // the sphere and was never hit; this covers the sky evenly and still
        // has no pole singularity to stretch stars into streaks.
        vec3 ad = abs(sd);
        vec2 uv;
        float face;
        if (ad.x >= ad.y && ad.x >= ad.z) {
            uv = sd.yz / ad.x;  face = sd.x > 0.0 ? 0.0 : 1.0;
        } else if (ad.y >= ad.z) {
            uv = sd.xz / ad.y;  face = sd.y > 0.0 ? 2.0 : 3.0;
        } else {
            uv = sd.xy / ad.z;  face = sd.z > 0.0 ? 4.0 : 5.0;
        }

        float stars = 0.0;
        // Two passes: a sparse field of bright stars over a dense faint one.
        for (int layer = 0; layer < 2; layer++) {
            float density = layer == 0 ? 24.0 : 46.0;
            float cutoff = layer == 0 ? 0.90 : 0.955;
            float size = layer == 0 ? 0.13 : 0.085;

            vec2 p = uv * density;
            vec2 cell = floor(p);
            vec2 f = fract(p);

            vec3 key = vec3(cell, face * 8.0 + float(layer) * 53.0);
            float pick = hash13(key);
            if (pick > cutoff) {
                vec2 jitter = hash33(key).xy * 0.7 + 0.15;
                float d = length(f - jitter);
                float mag = (pick - cutoff) / (1.0 - cutoff);
                float twinkle = 0.62 + 0.38 * sin(uTime * 1.7 + pick * 120.0);
                stars += smoothstep(size, 0.0, d) * mag * twinkle;
            }
        }

        // Fade out into the horizon haze, and out entirely at dawn.
        float horizonFade = smoothstep(-0.05, 0.22, dir.y);
        col += vec3(0.92, 0.94, 1.0) * stars * horizonFade * uNight * 2.3;
    }

    gl_FragColor = vec4(col, 1.0);
}
`;

export function createSky(env) {
    const material = new THREE.ShaderMaterial({
        vertexShader: VERT,
        fragmentShader: FRAG,
        side: THREE.BackSide,
        depthWrite: false,
        uniforms: {
            uSkyColor: env.uniforms.uSkyColor,
            uHorizonColor: env.uniforms.uHorizonColor,
            uSunColor: env.uniforms.uSunColor,
            uSunDir: env.uniforms.uSunDir,
            uNight: env.uniforms.uNight,
            uTime: env.uniforms.uTime,
        },
    });

    const mesh = new THREE.Mesh(new THREE.SphereGeometry(1, 32, 24), material);
    mesh.frustumCulled = false;
    mesh.renderOrder = -1000;
    mesh.name = 'sky';
    return mesh;
}
